import { Dot, parseColor, Text } from '@quality24/design-system';
import React from 'react';

import classNames from 'classnames';
import { Customizations, TableCellRender, ValueMap } from '../../../../@types';
import { isMappedLabel } from '../../../../utils/dataUtils';
import { ComparisonData } from './types';
import { CustomCellProps } from './CustomCell';

export interface Props {
  config: TableCellRender;
  formattedValue: string | number;
  value: number;
  /** The column mapping, needed to tell a mapped label from a formatted value */
  mapping?: Array<ValueMap>;
  customCellComponents?: Customizations['customCellComponents'];
}

export const CustomFooter: React.FunctionComponent<Props> = ({
  config: { compareTo, custom },
  formattedValue,
  value,
  mapping,
  customCellComponents,
}) => {
  // Handle the compareTo case
  const comparisonData = React.useMemo<ComparisonData | null>(() => {
    if (!compareTo) return null;

    let comparison = value || NaN;
    if (compareTo.type === 'zero') {
      comparison = value;
    }

    // Convert to color
    if (Number.isNaN(comparison)) return null;

    // A mapped label stands in for the value (e.g. a sub-second total formatted
    // as `00:00:00` and mapped to `-`), so there is no number left to sign. Only
    // the 'signs' renderer concatenates the prefix onto the body.
    if (compareTo.as === 'signs' && isMappedLabel(formattedValue, mapping)) {
      return { color: 'inherit', value: comparison, prefix: '' };
    }

    if (comparison > 0) {
      return {
        backgroundColor: 'red100_28',
        color: 'red500',
        value: comparison,
        prefix: '+',
      };
    }
    if (comparison < 0) {
      return {
        backgroundColor: 'green100_28',
        color: 'green500',
        value: comparison,
        prefix: '',
      };
    }
    return { color: 'inherit', value: comparison, prefix: '' };
  }, [compareTo, value, formattedValue, mapping]);

  return (
    <>
      {/* Comparison with dot */}
      {compareTo && compareTo.as !== 'signs' && (
        <>
          <Text className="flex-grow-1" size="sm2" weight="bold">
            {formattedValue}
          </Text>
          {comparisonData?.color && (
            <Dot color={parseColor(comparisonData.color)} />
          )}
        </>
      )}

      {/* Comparison with signs */}
      {compareTo && compareTo.as === 'signs' && (
        <div
          className={classNames(
            'd-flex flex-items-center flex-justify-center p-2 rounded-md',
            `bg-${comparisonData?.backgroundColor}`,
          )}
        >
          <Text
            size="sm2"
            color={comparisonData?.color}
            weight="bold"
            as="span"
          >
            {`${comparisonData?.prefix ?? ''}${formattedValue}`}
          </Text>
        </div>
      )}

      {/* Custom component */}
      {custom &&
        (() => {
          const { component, map, ...props } = custom;
          if (customCellComponents && customCellComponents[component]) {
            const Component = customCellComponents[
              component
            ] as typeof React.Component<CustomCellProps>;
            return (
              <Component
                cell={{ value } as CustomCellProps['cell']}
                map={map}
                formattedValue={formattedValue}
                {...props}
              />
            );
          }
          return null;
        })()}
    </>
  );
};

export default CustomFooter;
